import { AGENT_OR_ADMIN } from '@/shared/authorization/permissionGroups';

import { lazyRoute } from './lazy-route';
import { sectionChildren } from './section-index-redirect';

import type { RouteObject } from 'react-router-dom';

// /operation/calculator
const revenuePrediction = lazyRoute(
  'calculator',
  () => import('@/modules/RevenuePredictionModule'),
  { handle: { label: 'Premium Quoter', icon: 'RequestPage' } }
);

// /operation/endorsements/active-endorsements
const activeEndorsements = lazyRoute(
  'active-endorsements',
  () => import('@/modules/EndorsementModule').then(m => ({ default: m.DrpActiveEndorsements })),
  { handle: { label: 'Active Endorsements' } }
);

// /operation/endorsements/coverage-requests
const coverageRequests = lazyRoute(
  'coverage-requests',
  () => import('@/modules/EndorsementModule').then(m => ({ default: m.DrpCoverageRequests })),
  { handle: { label: 'Coverage Requests' } }
);

// /operation/endorsements/aip-sync — agent/admin only, mirroring the module's tab gating
const aipSync = lazyRoute(
  'aip-sync',
  () => import('@/modules/EndorsementModule').then(m => ({ default: m.DrpAipSync })),
  { handle: { label: 'AIP Sync', gate: AGENT_OR_ADMIN } }
);

// /operation/endorsements
const endorsements = lazyRoute('endorsements', () => import('@/modules/EndorsementModule'), {
  handle: { label: 'Endorsements', icon: 'AssignmentInd' },
  children: sectionChildren([activeEndorsements, coverageRequests, aipSync])
});

// /operation/overview/projection-charts
const projectionCharts = lazyRoute(
  'projection-charts',
  () => import('@/containers/ProjectedProductionContainer/ProjectedProductionContainer'),
  { handle: { label: 'Projection Charts' } }
);

// /operation/overview/data
const data = lazyRoute(
  'data',
  () =>
    import('@/features/drp/operation-overview-data-table/components/operation-overview-data-table').then(
      m => ({ default: m.OperationOverviewDataTable })
    ),
  { handle: { label: 'Data' } }
);

// /operation/overview/preferences
const preferences = lazyRoute(
  'preferences',
  () => import('@/containers/OperationPreferencesContainer/OperationPreferencesContainer'),
  { handle: { label: 'Preferences' } }
);

// /operation/overview/price-report
const priceReport = lazyRoute(
  'price-report',
  () => import('@/containers/OperationPriceReportContainer/OperationPriceReportContainer'),
  { handle: { label: 'Price Report' } }
);

// /operation/overview/aip
const aip = lazyRoute('aip', () => import('@/containers/AIPContainer/AIPContainer'), {
  handle: { label: 'AIP' }
});

// /operation/overview/contacts
const contacts = lazyRoute('contacts', () => import('@/scenes/lrp/components/ContactsComponent'), {
  handle: { label: 'Contacts' }
});

// /operation/overview/tags
const tags = lazyRoute(
  'tags',
  () => import('@/features/tags/tags-management').then(m => ({ default: m.Tags })),
  { handle: { label: 'Tags' } }
);

// /operation/overview
const operationOverview = lazyRoute('overview', () => import('@/modules/OperationOverviewModule'), {
  handle: { label: 'Operation Overview', icon: 'Dashboard' },
  children: sectionChildren([projectionCharts, data, preferences, priceReport, aip, contacts, tags])
});

// /operation/market/price-trends
const priceTrends = lazyRoute(
  'price-trends',
  () => import('@/containers/PriceTrendsContainer/PriceTrendsContainer'),
  { handle: { label: 'Price Trends' } }
);

// /operation/market/alerts
const alerts = lazyRoute('alerts', () => import('@/features/alerts'), {
  handle: { label: 'Alerts' }
});

// /operation/market/calendar
const calendar = lazyRoute('calendar', () => import('@/containers/DRPCalendar/DRPCalendar'), {
  handle: { label: 'Calendar' }
});

// /operation/market/price-reports
const priceReports = lazyRoute(
  'price-reports',
  () => import('@/containers/PriceReportsContainer/PriceReportsContainer'),
  { handle: { label: 'Price Reports' } }
);

// /operation/market/drp-sales-statistics
const drpSalesStatistics = lazyRoute(
  'drp-sales-statistics',
  () => import('@/containers/DrpSalesStatistics/DrpSalesStatistics'),
  { handle: { label: 'DRP Sales Statistics' } }
);

// /operation/market
const market = lazyRoute('market', () => import('@/modules/MarketDynamicsModule'), {
  handle: { label: 'Market Dynamics', icon: 'NorthEast' },
  children: sectionChildren([priceTrends, alerts, calendar, priceReports, drpSalesStatistics])
});

// /operation/scenario/endorsements
const scenarioEndorsements = lazyRoute(
  'endorsements',
  () => import('@/components/ScenarioAnalysis/EndorsementSimulation'),
  { handle: { label: 'Endorsements' } }
);

// /operation/scenario/projection-table
const scenarioProjectionTable = lazyRoute(
  'projection-table',
  () => import('@/components/ScenarioAnalysis/ScenarioAnalysisProjectionTable'),
  { handle: { label: 'Projection Table' } }
);

// /operation/scenario/data
const scenarioData = lazyRoute(
  'data',
  () =>
    import('@/containers/ScenarioAnalysisDataContainer/ScenarioAnalysisDataContainer').then(m => ({
      default: m.ScenarioAnalysisDataContainer
    })),
  { handle: { label: 'Data' } }
);

// /operation/scenario
const scenario = lazyRoute('scenario', () => import('@/modules/ScenarioAnalysisModule'), {
  handle: { label: 'Scenario Analysis', icon: 'Compare' },
  children: sectionChildren([scenarioEndorsements, scenarioProjectionTable, scenarioData])
});

// /operation
export const drpRoutes: RouteObject = lazyRoute(
  '/operation/:name',
  () => import('@/modules/OperationScreenModule'),
  { children: [revenuePrediction, endorsements, operationOverview, market, scenario] }
);
